import {ChangeEvent, MouseEvent, useCallback, useEffect, useState} from "react";
import {SchemaItem} from "../listan-backend-api";
import AddItem from "./AddItem";
import {CheckedItemPosition, CheckedItemsListSortOrder, ListOptions} from "../ClientSidetypes";

type SchemaItemWithGroupDetails = {
    itemData: SchemaItem,
    groupNumber: number
}

// We put it here so that safari only loads the sound once
let fetchedArrayBuffer: ArrayBuffer

fetch("./click-345983-short.mp3")
    .then((response) => response.arrayBuffer())
    .then(arrayBuffer => {fetchedArrayBuffer = arrayBuffer;});

export default function CheckedItemsList(
    {
        items,
        setCheckStatus,
        removeItem,
        updateItemText,
        listOptions,
        disabled,
        onChange,
    } : {
        items: SchemaItem[],
        setCheckStatus: (uuid: string, checked: boolean) => void,
        removeItem: (uuid: string) => void,
        updateItemText: (uuid: string, text: string) => void,
        listOptions: ListOptions
        disabled: boolean
        onChange: (newListOptions: ListOptions) => void
    }
) {

    const mapToSortable = (itemsToMap: SchemaItem[]) : SchemaItemWithGroupDetails[]  => {
        return itemsToMap.map((item) => {
            return {itemData: item, groupNumber: 999};
        }) as SchemaItemWithGroupDetails[];
    }

    const [itemIdToEdit, setItemIdToEdit] = useState("");
    const [itemIdToEditLastChanged, setItemIdToEditLastChanged] = useState(0);
    const [copyOfitems, setCopyOfItems] = useState(mapToSortable(items));

    const sortByAddedAt = (a : SchemaItemWithGroupDetails, b : SchemaItemWithGroupDetails) => {
        if (new Date(a.itemData.addedAt) === new Date(b.itemData.addedAt)) {
            return 0;
        }

        if (new Date(a.itemData.addedAt) > new Date(b.itemData.addedAt)) {
            return 1;
        } else {
            return -1;
        }
    }

    const sortByCheckedLastChangedAt = (a : SchemaItemWithGroupDetails, b : SchemaItemWithGroupDetails) => {
        if (new Date(a.itemData.checkedLastChangedAt) === new Date(b.itemData.checkedLastChangedAt)) {
            return 0;
        }

        if (new Date(a.itemData.checkedLastChangedAt) > new Date(b.itemData.checkedLastChangedAt)) {
            return -1;
        } else {
            return 1;
        }
    }

    useEffect(() => {
        let toSort = mapToSortable(items);

        toSort.sort(sortByAddedAt);

        toSort = toSort.filter((a ) => a.itemData.checked);


        if (listOptions.checkedItemsListSortOrder === CheckedItemsListSortOrder.CheckedAt) {
            toSort.sort(sortByCheckedLastChangedAt);
        }

        if (listOptions.checkedItemsListSortOrder === CheckedItemsListSortOrder.Alpanumeric) {
            toSort.sort((a, b) => a.itemData.text.localeCompare(b.itemData.text, "sv"));
        }

        setCopyOfItems(toSort);

    }, [listOptions, items]);

    const updateCheckStatus = (event : ChangeEvent<HTMLInputElement>) => {
        setCheckStatus(event.target.name, event.target.checked)
    };

    const removeClick = (event: MouseEvent<HTMLButtonElement> ) => {
        removeItem(event?.currentTarget?.name);
    };

    const editClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        if (event?.currentTarget?.name) {
            const nameToChangeTo = event.currentTarget.name;

            // We do it if it was more than 100ms ago
            if (itemIdToEditLastChanged + 100 < Date.now()) {
                setItemIdToEdit((currentValue) => {
                    if (nameToChangeTo === currentValue) {
                        return "";
                    } else {
                        return nameToChangeTo;
                    }
                });
            }
        }
    }, [itemIdToEditLastChanged])

    const updateItem = (newValue: string) => {
        updateItemText(itemIdToEdit, newValue);
        setItemIdToEdit("");
        setItemIdToEditLastChanged(Date.now());
    };

    const play = () =>{
        if (listOptions.playSoundWhenCheckedAndUncheked) {
            if (fetchedArrayBuffer) {
                // TODO, It would be nice to reuse the context and audio buffer
                const audioContext = new AudioContext();
                audioContext.decodeAudioData(structuredClone(fetchedArrayBuffer)).then((audioBuffer) => {
                    const source = audioContext.createBufferSource();
                    source.buffer = audioBuffer;
                    source.connect(audioContext.destination);
                    source.start();

                    return source;
                });
            }
        }
    };

    const radioButtonChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange({...listOptions, checkedItemsListSortOrder: event.target.value} as ListOptions);
    };

    return listOptions.checkedItemPosition !== CheckedItemPosition.CheckedItemsList ? <></> : (
        <section id={"checked-items-list"}>
            <h2>Handlat</h2>
            <menu>
                <li>
                    <label>
                        <input
                            type={"radio"}
                            name={"checked-items-list-sort-order"}
                            value={CheckedItemsListSortOrder.CheckedAt}
                            onChange={radioButtonChange}
                            checked={listOptions.checkedItemsListSortOrder === CheckedItemsListSortOrder.CheckedAt}
                        />
                        Senaste
                    </label>
                </li>
                <li>
                    <label>
                        <input
                            type={"radio"}
                            name={"checked-items-list-sort-order"}
                            value={CheckedItemsListSortOrder.Alpanumeric}
                            onChange={radioButtonChange}
                            checked={listOptions.checkedItemsListSortOrder === CheckedItemsListSortOrder.Alpanumeric}
                        />
                        A till Ö
                    </label>
                </li>
            </menu>
            <ul id={"theList"} className={disabled ? "disabled" : ""}>
                {
                    copyOfitems.map((item) => {
                        return ( item.itemData.removed ? "" :
                                <li key={item.itemData.id} className={item.itemData.checked ? "checked" : ""}>
                                    <input disabled={disabled} type={"checkbox"} checked={item.itemData.checked} id={item.itemData.id + "_label"} name={item.itemData.id} onChange={updateCheckStatus} onClick={play}/>
                                    <label htmlFor={item.itemData.id + "_label"} onClick={play}>
                                        {
                                            item.itemData.id === itemIdToEdit ? <AddItem disabled={disabled} forceFocus={true} addToList={updateItem} startingText={item.itemData.text} /> : item.itemData.text
                                        }
                                    </label>
                                    <menu>
                                        <button  disabled={disabled} className={"edit"} name={item.itemData.id} onClick={editClick}>Ändra</button>
                                        <button  disabled={disabled} className={"remove"} name={item.itemData.id} onClick={removeClick}>Ta bort</button>
                                    </menu>
                                </li>
                        );
                    })
                }
            </ul>
        </section>
    );
}